/**
 * Todo 任务清单面板组件 (AI Elements Migration)
 *
 * 使用原生 HTML + Tailwind CSS 构建任务清单展示
 * 默认折叠状态：只显示进度概览，点击展开查看详细任务列表
 *
 * @module TodoPanel
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useChatStore } from '../../store/chatStore';
import type { TodoItem, TodoStatus, TodoPriority } from '../../types/chat';

// ==================== 类型定义 ====================

export interface TodoPanelProps {
  /** 最大显示任务数 */
  maxVisible?: number;
}

// ==================== 状态配置 ====================

/** 任务状态样式配置 */
const STATUS_CONFIG: Record<TodoStatus, { label: string; progressStatus: 'active' | 'success' | 'error' }> = {
  pending: {
    label: '待处理',
    progressStatus: 'active'
  },
  in_progress: {
    label: '进行中',
    progressStatus: 'active'
  },
  completed: {
    label: '已完成',
    progressStatus: 'success'
  },
  cancelled: {
    label: '已取消',
    progressStatus: 'error'
  }
};

/** 优先级样式配置 */
const PRIORITY_CONFIG: Record<TodoPriority, { label: string; className: string }> = {
  high: {
    label: '高',
    className: 'text-red-600 bg-red-50'
  },
  medium: {
    label: '中',
    className: 'text-yellow-600 bg-yellow-50'
  },
  low: {
    label: '低',
    className: 'text-gray-500 bg-gray-50'
  }
};

/** 状态图标组件 */
const StatusIcon: React.FC<{ status: TodoStatus }> = ({ status }) => {
  const iconClass = 'w-4 h-4 flex-shrink-0';

  switch (status) {
    case 'pending':
      return (
        <svg className={`${iconClass} text-gray-400`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" strokeWidth={2} />
        </svg>
      );
    case 'in_progress':
      return (
        <svg className={`${iconClass} text-blue-500 animate-spin`} fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={4} />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      );
    case 'completed':
      return (
        <svg className={`${iconClass} text-green-500`} fill="currentColor" viewBox="0 0 24 24">
          <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
        </svg>
      );
    case 'cancelled':
      return (
        <svg className={`${iconClass} text-gray-400`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
  }
};

// ==================== 子组件 ====================

/**
 * 进度条组件
 */
const ProgressBar: React.FC<{ value: number; status: 'active' | 'success' | 'error' }> = ({ value, status }) => {
  const bgColor = status === 'success' ? 'bg-green-500' : status === 'error' ? 'bg-red-500' : 'bg-blue-500';
  
  return (
    <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
      <div 
        className={`h-full rounded-full transition-all duration-150 ${bgColor}`}
        style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
      />
    </div>
  );
};

/**
 * 任务项渲染组件
 */
const TodoItemContent: React.FC<{ todo: TodoItem; index: number }> = ({ todo, index }) => {
  const statusConfig = STATUS_CONFIG[todo.status];
  const priorityConfig = PRIORITY_CONFIG[todo.priority];
  
  return (
    <div className="flex items-start gap-3 w-full py-3 px-4 border-b border-gray-100 last:border-b-0">
      {/* 状态图标 */}
      <span className="mt-0.5">
        <StatusIcon status={todo.status} />
      </span>
      
      {/* 任务内容 */}
      <div className="flex-1 min-w-0">
        <p className={`text-sm ${todo.status === 'completed' ? 'text-gray-400 line-through' : 'text-gray-800'}`}>
          {todo.content}
        </p>
        <div className="flex items-center gap-2 mt-1.5">
          {/* 状态标签 */}
          <span className={`text-xs px-1.5 py-0.5 rounded ${
            todo.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
            todo.status === 'completed' ? 'bg-green-100 text-green-700' :
            todo.status === 'cancelled' ? 'bg-gray-100 text-gray-500' :
            'bg-gray-100 text-gray-600'
          }`}>
            {statusConfig.label}
          </span>
          {/* 优先级标签 */}
          <span className={`text-xs px-1.5 py-0.5 rounded ${priorityConfig.className}`}>
            {priorityConfig.label}优先级
          </span>
        </div>
      </div>
      
      {/* 序号 */}
      <span className="text-xs text-gray-400 flex-shrink-0">
        #{index + 1}
      </span>
    </div>
  );
};

// ==================== 主组件 ====================

/**
 * Todo 任务清单面板组件
 *
 * 使用原生 HTML + Tailwind CSS 构建，提供：
 * - 卡片容器
 * - 进度条
 * - 任务列表
 *
 * 默认折叠状态：只显示进度概览
 * 点击展开：显示完整任务列表
 */
export const TodoPanel: React.FC<TodoPanelProps> = ({
  maxVisible = 10
}) => {
  // 默认折叠状态
  const [isExpanded, setIsExpanded] = useState(false);
  const todos = useChatStore((state) => state.todos);

  // 计算统计数据
  const stats = useMemo(() => {
    const total = todos.length;
    const completed = todos.filter((t) => t.status === 'completed').length;
    const inProgress = todos.filter((t) => t.status === 'in_progress').length;
    const pending = todos.filter((t) => t.status === 'pending').length;
    const cancelled = todos.filter((t) => t.status === 'cancelled').length;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { total, completed, inProgress, pending, cancelled, percentage };
  }, [todos]);

  // 按状态排序：in_progress > pending > completed > cancelled
  const sortedTodos = useMemo(() => {
    const order: Record<TodoStatus, number> = {
      in_progress: 0,
      pending: 1,
      completed: 2,
      cancelled: 3
    };
    return [...todos].sort((a, b) => order[a.status] - order[b.status]);
  }, [todos]);

  const displayTodos = sortedTodos.slice(0, maxVisible);

  // 切换展开/收起
  const handleToggle = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  // 如果没有任务，不显示面板
  if (stats.total === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* 头部 - 点击展开/折叠 */}
      <button
        onClick={handleToggle}
        className="w-full px-4 py-3 hover:bg-gray-50 transition-colors flex items-center justify-between gap-3"
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {/* 任务图标 */}
          <span className="p-1.5 rounded-md bg-indigo-100 text-indigo-600 flex-shrink-0">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          </span>

          {/* 标题和进度 */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-gray-900">
                任务清单
              </span>
              <span className="text-xs text-gray-500">
                {stats.completed}/{stats.total}
              </span>
            </div>
            {/* 进度条组件 */}
            <ProgressBar
              value={stats.percentage}
              status={stats.percentage === 100 ? 'success' : 'active'}
            />
          </div>

          {/* 进行中标签 */}
          {stats.inProgress > 0 && (
            <span className="px-2 py-0.5 text-xs rounded-full font-medium bg-blue-100 text-blue-700 animate-pulse flex-shrink-0">
              {stats.inProgress} 个进行中
            </span>
          )}

          {/* 展开/收起图标 */}
          <svg
            className={`w-4 h-4 text-gray-500 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* 折叠状态 - 显示进度概览 */}
      {!isExpanded && (
        <div className="px-4 py-2 border-t border-gray-100 bg-gray-50/50">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-400">点击展开查看详情</span>
            <div className="flex items-center gap-3">
              {stats.pending > 0 && (
                <span className="flex items-center gap-1">
                  <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" strokeWidth={2} />
                  </svg>
                  <span className="text-gray-500">待处理 {stats.pending}</span>
                </span>
              )}
              {stats.inProgress > 0 && (
                <span className="flex items-center gap-1">
                  <svg className="w-3 h-3 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={4} />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span className="text-blue-600 font-medium">进行中 {stats.inProgress}</span>
                </span>
              )}
              {stats.completed > 0 && (
                <span className="flex items-center gap-1">
                  <svg className="w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 24 24">
                    <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
                  </svg>
                  <span className="text-green-600">已完成 {stats.completed}</span>
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 任务列表 - 展开时显示 */}
      {isExpanded && (
        <div className="border-t border-gray-100">
          {/* 任务列表 */}
          <div className="divide-y divide-gray-100">
            {displayTodos.map((todo, index) => (
              <TodoItemContent key={`${todo.content}-${index}`} todo={todo} index={index} />
            ))}
          </div>

          {/* 显示更多提示 */}
          {sortedTodos.length > maxVisible && (
            <div className="text-center text-xs text-gray-500 py-2 border-t border-gray-100">
              显示前 {maxVisible} 项，共 {sortedTodos.length} 项
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TodoPanel;
